import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Search, Phone, MessageSquare, Edit3, Send, X, Users, Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';

const TYPE_FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'internal',   label: 'Internal' },
  { id: 'external',   label: 'External' },
  { id: 'unassigned', label: 'Unassigned' },
];

const TYPE_BADGE = {
  internal:   'bg-blue-500/15 text-blue-600',
  external:   'bg-purple-500/15 text-purple-600',
  unassigned: 'bg-gray-500/15 text-gray-600',
};

export default function MasterContactsPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [holderId, setHolderId] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // contact being edited (external only)
  const [editDraft, setEditDraft] = useState({});
  const [sending, setSending] = useState(null);   // contact being shared
  const [shareUserId, setShareUserId] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (holderId && holderId !== 'all') params.holder_id = holderId;
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get('/admin/master-contacts', { params });
      setItems(res.data.items || []);
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error('Could not load master contacts');
    }
    setLoading(false);
  }, [api, holderId, typeFilter, search]);

  useEffect(() => { if (isAdmin) load(); }, [load, isAdmin]);

  // Mobile header — title "Master Contacts" + back arrow to /calls.
  // The in-page back button + title row is gone now.
  usePageHeader({ title: 'Master Contacts', back: '/calls' }, []);

  const holderOptions = useMemo(() => {
    const opts = [{ id: 'all', name: 'All contact holders' }, { id: '__unassigned__', name: 'Unassigned' }];
    return [...opts, ...users.map((u) => ({ id: u.id, name: u.name }))];
  }, [users]);

  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-muted-foreground">Admin access required.</div>
    );
  }

  const handleCall = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const handleText = (contact) => {
    if (!contact.phone) return toast.error('No phone number');
    navigate(`/messages?to=${encodeURIComponent(contact.phone)}`);
  };

  const handleEdit = (contact) => {
    if (contact.type !== 'external') {
      return toast.error('Only external contacts can be edited here.');
    }
    setEditing(contact);
    setEditDraft({
      name: contact.name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      company: contact.company || '',
      notes: contact.notes || '',
    });
  };

  const submitEdit = async () => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await api.put(`/admin/master-contacts/external/${editing.id}`, editDraft);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
    setSubmitting(false);
  };

  const submitShare = async () => {
    if (!sending || !shareUserId) return;
    setSubmitting(true);
    try {
      await api.post('/admin/master-contacts/send', {
        contact_id: sending.id,
        contact_type: sending.type,
        target_user_id: shareUserId,
        note: shareNote.trim() || null,
      });
      setSending(null);
      setShareUserId('');
      setShareNote('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 space-y-3" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid="master-contacts-page">
      {/* Filters */}
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-3 space-y-2 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email, notes, address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            style={{ fontSize: '16px' }}
            data-testid="master-search"
          />
        </div>
        <div className="flex gap-2">
          <Select value={holderId} onValueChange={setHolderId}>
            <SelectTrigger className="flex-1" data-testid="master-holder-filter">
              <SelectValue placeholder="Contact holder" />
            </SelectTrigger>
            <SelectContent>
              {holderOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTypeFilter(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${typeFilter === t.id ? 'bg-[#586D52] text-white' : 'bg-gray-100 dark:bg-[#252525] text-muted-foreground'}`}
              data-testid={`master-type-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-center py-10 text-muted-foreground">No contacts match your filters.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{items.length} contact{items.length === 1 ? '' : 's'}</p>
          {items.map((c) => (
            <div key={`${c.type}-${c.id}-${c.holder_id}`} className="rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm p-3 space-y-2" data-testid={`master-row-${c.type}-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{c.name || '(unnamed)'}</p>
                    <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${TYPE_BADGE[c.type] || ''}`}>{c.type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Holder: {c.holder_name || '—'}</p>
                  {(c.phone || c.email) && (
                    <p className="text-xs text-muted-foreground truncate">{c.phone}{c.phone && c.email ? ' · ' : ''}{c.email}</p>
                  )}
                  {c.company && <p className="text-xs text-muted-foreground">Company: {c.company}</p>}
                  {c.notes && <p className="text-xs text-muted-foreground line-clamp-2">{c.notes}</p>}
                  {(c.addresses || []).length > 0 && (
                    <div className="mt-1">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#586D52]">Addresses</p>
                      <ul className="text-xs text-muted-foreground list-disc pl-4">
                        {c.addresses.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 pt-1 border-t border-gray-100 dark:border-gray-800">
                <button onClick={() => handleCall(c.phone)} disabled={!c.phone} className="flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-green-500/15 text-green-700 disabled:opacity-30" data-testid={`master-call-${c.id}`}>
                  <Phone className="w-3 h-3" /> Call
                </button>
                <button onClick={() => handleText(c)} disabled={!c.phone} className="flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-purple-500/15 text-purple-700 disabled:opacity-30" data-testid={`master-text-${c.id}`}>
                  <MessageSquare className="w-3 h-3" /> Text
                </button>
                <button onClick={() => handleEdit(c)} disabled={c.type !== 'external'} className="flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-gray-500/15 text-gray-700 disabled:opacity-30" data-testid={`master-edit-${c.id}`}>
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => setSending(c)} className="ml-auto flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-[#586D52]/15 text-[#586D52]" data-testid={`master-send-${c.id}`}>
                  <Send className="w-3 h-3" /> Send
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)]">
          <DialogHeader><DialogTitle>Edit Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={editDraft.name || ''} onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))} data-testid="master-edit-name" />
            <Input placeholder="Phone" value={editDraft.phone || ''} onChange={(e) => setEditDraft((p) => ({ ...p, phone: e.target.value }))} data-testid="master-edit-phone" />
            <Input placeholder="Email" value={editDraft.email || ''} onChange={(e) => setEditDraft((p) => ({ ...p, email: e.target.value }))} data-testid="master-edit-email" />
            <Input placeholder="Company" value={editDraft.company || ''} onChange={(e) => setEditDraft((p) => ({ ...p, company: e.target.value }))} data-testid="master-edit-company" />
            <textarea
              placeholder="Notes"
              value={editDraft.notes || ''}
              onChange={(e) => setEditDraft((p) => ({ ...p, notes: e.target.value }))}
              className="w-full min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm"
              data-testid="master-edit-notes"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={submitEdit} disabled={submitting} data-testid="master-edit-save">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share-via-DM dialog */}
      <Dialog open={!!sending} onOpenChange={(o) => !o && setSending(null)}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)]">
          <DialogHeader><DialogTitle>Send Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Send <span className="font-semibold">{sending?.name}</span> as an internal direct message.</p>
            <Select value={shareUserId} onValueChange={setShareUserId}>
              <SelectTrigger data-testid="master-share-target"><SelectValue placeholder="Choose recipient" /></SelectTrigger>
              <SelectContent>
                {users.filter((u) => u.id !== user?.id).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <textarea
              placeholder="Optional note…"
              value={shareNote}
              onChange={(e) => setShareNote(e.target.value)}
              className="w-full min-h-[80px] rounded-md border bg-background px-3 py-2 text-sm"
              data-testid="master-share-note"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setSending(null)}>Cancel</Button>
              <Button onClick={submitShare} disabled={!shareUserId || submitting} data-testid="master-share-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
